import { asGlobalResourceQuery } from 'v5/api/_common/query/workspace-scoping';
import { maybe } from '@passionware/monads';
import { parseWithDecoratedError } from 'v4/entities/common/parseWithDecoratedError';
import { convertBooleanFilterToApi } from 'v5/platform/filters/filterConverters';
import { z } from 'zod';
import { Resource, Shortlist } from '../../../__types__';
import { ApiClient } from '../../../lib/api/ApiClient.types';
import { convertCoreFieldFiltersToHttp } from '../../core/core-fields/core-fields-filters.http-converters';
import {
  convertCustomFieldFiltersToHttp,
  FieldNameTypeMapping
} from '../../core/custom-fields/custom-fields-filters.http-converters';
import { mergeWorkspaceScopingIntoHttpFilters } from 'v5/core/app-scope/query/mergeWorkspaceScopingIntoHttpFilters';
import { mergeTeamScopingIntoHttpFilters } from 'v5/core/app-scope/query/mergeTeamScopingIntoHttpFilters';
import { MergeServices, WithServices } from '../../platform/ts/services';
import { WithMetastructureMappingService } from '../../services/internal/MetastructureMappingService/MetastructureMappingService';
import {
  BulkArchiveRequest,
  ShortlistApi,
  ShortlistQuery,
  ShortlistResourcesSearchQuery,
  ShortlistScopedQuery,
  asGlobalShortlistQuery
} from './shortlist.api';
import { shortlist$, shortlistFromHttp } from './shortlist.http.schema';
import { getResourceListRequest } from '../resource/resource.api.http';

const mapShortlistSortFieldToApi = (field: string): string => {
  const fieldMap: Record<string, string> = {
    createdAt: 'created_at',
    updatedAt: 'updated_at',
    title: "attributes->>'title'",
    publishedAt: 'published_at'
  };
  return fieldMap[field] ?? field;
};

export function getShortlistListRequest(
  query: ShortlistQuery,
  fieldMappings: FieldNameTypeMapping
): Record<string, unknown> {
  const scopedQuery: ShortlistScopedQuery =
    'workspaceId' in query && 'teamId' in query
      ? query
      : asGlobalShortlistQuery(query);

  const filters = {
    published: maybe.map(
      scopedQuery.filters.published,
      convertBooleanFilterToApi
    ),
    ...convertCoreFieldFiltersToHttp(scopedQuery.filters),
    ...convertCustomFieldFiltersToHttp(
      fieldMappings,
      scopedQuery.filters,
      scopedQuery.workspaceId
    ),
    ...mergeWorkspaceScopingIntoHttpFilters(scopedQuery),
    ...mergeTeamScopingIntoHttpFilters(scopedQuery)
  };

  return {
    query: scopedQuery.search,
    p: scopedQuery.page.page + 1,
    count: scopedQuery.page.pageSize,
    order_by: scopedQuery.sort
      ? {
          field: mapShortlistSortFieldToApi(scopedQuery.sort.field),
          direction: scopedQuery.sort.order.toUpperCase(),
          enterprise_workspace_id: scopedQuery.workspaceId
        }
      : {
          field: 'created_at',
          direction: 'DESC',
          enterprise_workspace_id: scopedQuery.workspaceId
        },
    includeResource: true,
    filters,
    archived: scopedQuery.filters?.archived?.value ? true : false
  };
}

async function searchShortlists(
  client: ApiClient,
  services: MergeServices<[WithMetastructureMappingService]>,
  query: ShortlistQuery
): Promise<{ items: Shortlist[]; count: number }> {
  const fieldMappings =
    await services.metastructureMappingService.getAllFieldNameTypeMappings();

  const response = await client.post('/search/shortlists', {
    data: getShortlistListRequest(query, fieldMappings),
    token: true
  });

  const responseSchema = z.object({
    results: z.array(shortlist$),
    paging: z.object({
      total: z.number(),
      page: z.number(),
      count_per_page: z.number()
    })
  });

  const parsed = parseWithDecoratedError(responseSchema, response);
  return {
    items: parsed.results.map(shortlistFromHttp),
    count: parsed.paging.total
  };
}

export function createShortlistApi(
  config: { client: ApiClient } & WithServices<
    [WithMetastructureMappingService]
  >
): ShortlistApi {
  const { client, services } = config;
  return {
    getShortlists: async query => {
      return searchShortlists(client, services, query);
    },

    getShortlist: async id => {
      const response = await client.get(`/shortlists/${id}`, { token: true });

      const parsed = parseWithDecoratedError(shortlist$, response);
      return shortlistFromHttp(parsed);
    },

    createShortlist: async request => {
      // As the legacy create modal: the public title and description (the
      // builder's hero, the client page) start as the internal ones.
      const response = await client.post('/shortlists', {
        data: {
          title: request.title,
          public_title: request.title,
          tags: request.tags,
          ...(request.description !== null
            ? {
                description: request.description,
                public_description: request.description
              }
            : {}),
          ...(request.enterpriseWorkspaceId !== null
            ? { enterprise_workspace_id: request.enterpriseWorkspaceId }
            : {}),
          ...(request.enterpriseTeamId !== null
            ? { enterprise_team_id: request.enterpriseTeamId }
            : {})
        },
        token: true
      });

      const parsed = parseWithDecoratedError(shortlist$, response);
      return shortlistFromHttp(parsed);
    },

    searchShortlistResources: async (query: ShortlistResourcesSearchQuery) => {
      // Get field type mappings for custom fields
      const fieldMappings =
        await services.metastructureMappingService.getAllFieldNameTypeMappings();

      const requestData = getResourceListRequest(
        asGlobalResourceQuery(query),
        fieldMappings
      );

      const response = await client.post<{
        results: Resource['id'][];
        paging: { total: number; page: number };
      }>(`/shortlists/${query.shortlistId}/contacts/search`, {
        data: requestData,
        token: true
      });

      return {
        items: response.results,
        count: response.paging.total
      };
    },

    bulkArchive: async (request: BulkArchiveRequest) => {
      // Archive shortlists by updating each one with archived: true
      await Promise.all(
        request.ids.map(id =>
          client.put(`/shortlists/${id}`, {
            data: { archived: true },
            token: true
          })
        )
      );
    },

    bulkUnarchive: async (request: BulkArchiveRequest) => {
      // Unarchive shortlists by updating each one with archived: false
      await Promise.all(
        request.ids.map(id =>
          client.put(`/shortlists/${id}`, {
            data: { archived: false },
            token: true
          })
        )
      );
    }
  };
}
